import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private translocoService = inject(TranslocoService);

  private currentLanguageSubject = new BehaviorSubject<string>('en');
  public currentLanguage$: Observable<string> = this.currentLanguageSubject.asObservable();

  /**
   * Supported languages. Add new entries when adding translations.
   */
  static readonly SUPPORTED_LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'pl', name: 'Polski' }
  ];

  constructor() {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    const storedLang = localStorage.getItem('locale');
    const initialLang = storedLang || this.getBrowserLanguage();
    this.setLanguage(initialLang);
  }

  /**
   * Set the active language and persist to localStorage.
   */
  setLanguage(lang: string): void {
    const supported = LanguageService.SUPPORTED_LANGUAGES.map(l => l.code);
    const validLang = supported.includes(lang) ? lang : 'en';

    this.translocoService.setActiveLang(validLang);
    this.currentLanguageSubject.next(validLang);
    localStorage.setItem('locale', validLang);
  }

  /**
   * Get the current active language code.
   */
  getCurrentLanguage(): string {
    return this.currentLanguageSubject.value;
  }

  /**
   * Called after login to sync user's DB preference.
   * Only switches if the user has a different locale set.
   */
  setUserLanguage(locale: string | undefined): void {
    if (locale && locale !== this.getCurrentLanguage()) {
      this.setLanguage(locale);
    }
  }

  private getBrowserLanguage(): string {
    const browserLang = navigator.language.split('-')[0];
    const supported = LanguageService.SUPPORTED_LANGUAGES.map(l => l.code);
    return supported.includes(browserLang) ? browserLang : 'en';
  }
}
